/*
 * Компонент AdminMessages — массовая рассылка сообщений пользователям
 *
 * Основные функции:
 * - Выбор фильтра целевой аудитории (все / активные / бывшие / новые без покупок)
 * - Выбор каналов отправки (Telegram, Email)
 * - Ввод темы письма (при активном канале Email)
 * - Ввод текста рассылки
 * - Валидация перед отправкой
 * - Отправка рассылки через MailingEndpoint
 * - Отображение количества отправленных сообщений
 */
<script setup lang="ts">
// Реактивность и вычисляемые свойства Vue
import { ref, computed } from 'vue'
// Определение размера экрана — для динамического padding
import { useDisplay } from 'vuetify'
// Утилита определения Telegram-контекста
import { isTelegram } from '@/utils/isTelegram'
// Стор администрирования — экшен отправки рассылки
import { useAdminStore } from '@/modules/admin/store/adminStore'
// Компонент заголовка секции
import SectionHeader from '@/components/SectionHeader.vue'
// Enum фильтров и методов рассылки из сгенерированных proto-стабов
import { MailingUserFilter, MailingMethod } from '@vpn-tg/vue-client-grpc/proto/mailing/mailing-endpoint'

// Инициализация стора администрирования
const adminStore = useAdminStore()
// Определение мобильного экрана для динамического padding
const { mobile } = useDisplay()

// Варианты фильтрации пользователей для v-select
const filterOptions = [
  { title: 'Все пользователи', value: MailingUserFilter.ALL },
  { title: 'Активные (с подпиской)', value: MailingUserFilter.ACTIVE },
  { title: 'Бывшие (подписка истекла)', value: MailingUserFilter.FORMER },
  { title: 'Новые без покупок', value: MailingUserFilter.NEW_NO_PURCHASE },
]

// Выбранные группы аудитории рассылки
const selectedFilters = ref<MailingUserFilter[]>([MailingUserFilter.ALL])

const onFiltersChange = (newFilters: MailingUserFilter[]) => {
  if (!newFilters || newFilters.length === 0) {
    selectedFilters.value = [MailingUserFilter.ALL]
    return
  }
  const lastSelected = newFilters[newFilters.length - 1]
  if (lastSelected === MailingUserFilter.ALL) {
    selectedFilters.value = [MailingUserFilter.ALL]
  } else if (newFilters.includes(MailingUserFilter.ALL)) {
    selectedFilters.value = newFilters.filter((f) => f !== MailingUserFilter.ALL)
  } else {
    selectedFilters.value = newFilters
  }
}

// Флаг отправки через Telegram (по умолчанию включён)
const sendViaTelegram = ref(true)
// Флаг отправки через Email (по умолчанию включён)
const sendViaEmail = ref(true)
// Тема письма (для Email-рассылки)
const subject = ref('')
// Текст сообщения для рассылки
const messageText = ref('')
// Количество успешно отправленных сообщений (null до первой отправки)
const sentCount = ref<number | null>(null)
// Флаг состояния загрузки при отправке рассылки
const isSending = ref(false)

/**
 * Список выбранных методов рассылки на основе чекбоксов
 */
const selectedMethods = computed<MailingMethod[]>(() => {
  const methods: MailingMethod[] = []
  if (sendViaTelegram.value) {
    methods.push(MailingMethod.TELEGRAM_MESSAGE)
  }
  if (sendViaEmail.value) {
    methods.push(MailingMethod.EMAIL)
  }
  return methods
})

/**
 * Валидация формы:
 * - Текст сообщения не должен быть пустым
 * - Должен быть выбран хотя бы один канал отправки
 */
const isFormValid = computed(() => {
  if (!messageText.value.trim()) return false
  if (selectedMethods.value.length === 0) return false
  return true
})

/**
 * Отправляет массовую рассылку сообщений
 *
 * Основные действия:
 * - Проверяет валидность формы
 * - Устанавливает состояние загрузки
 * - Вызывает adminStore.sendMailing с выбранными параметрами
 * - Сохраняет количество отправленных сообщений для отображения
 */
const sendMailing = async () => {
  if (!isFormValid.value) return

  isSending.value = true
  sentCount.value = null

  const result = await adminStore.sendMailing(
    selectedFilters.value,
    messageText.value,
    selectedMethods.value,
    subject.value,
  )
  sentCount.value = result
  isSending.value = false
}
</script>

<template>
  <div class="d-flex flex-column ga-4 pt-4" :class="{ 'pa-4': isTelegram() || mobile }">
    <header>
      <section-header />
    </header>

    <v-select
      :model-value="selectedFilters"
      :items="filterOptions"
      item-title="title"
      item-value="value"
      label="Целевая аудитория (группы)"
      variant="outlined"
      multiple
      chips
      closable-chips
      hide-details
      @update:model-value="onFiltersChange"
    />

    <div>
      <div class="text-subtitle-2 text-medium-emphasis mb-2">Каналы отправки</div>
      <div class="d-flex flex-wrap ga-4">
        <v-checkbox
          v-model="sendViaTelegram"
          label="Telegram"
          color="primary"
          density="compact"
          hide-details
        />
        <v-checkbox
          v-model="sendViaEmail"
          label="Email"
          color="primary"
          density="compact"
          hide-details
        />
      </div>
    </div>

    <v-text-field
      v-if="sendViaEmail"
      v-model="subject"
      label="Тема письма"
      variant="outlined"
      hide-details
    />

    <v-textarea
      v-model="messageText"
      label="Текст сообщения"
      variant="outlined"
      rows="5"
      hide-details
    />

    <v-btn
      color="primary"
      variant="flat"
      block
      :loading="isSending"
      :disabled="!isFormValid"
      @click="sendMailing"
    >
      Отправить
    </v-btn>

    <v-alert
      v-if="sentCount !== null"
      type="success"
      variant="tonal"
    >
      Отправлено сообщений: {{ sentCount }}
    </v-alert>
  </div>
</template>

<style scoped></style>
